import type { ReactNode } from 'react';

interface LinkOrTextProps {
  /** Destination URL; when null/undefined the children render as plain text. */
  href: string | null | undefined;
  /** Class applied to the anchor (each caller passes its own SCSS module link class). */
  className?: string;
  children: ReactNode;
}

/**
 * Renders `children` as a new-tab link when an href is present, otherwise as
 * plain text. Centralizes the "link-or-plaintext cell" used across the Compare
 * feature so the target/rel hardening lives in exactly one place.
 *
 * These hrefs are backend-absolute, cross-app paths (full page loads), so a
 * plain `<a>` — not a react-router `<Link>` — is the correct element.
 * @param {LinkOrTextProps} props - href, optional className, and children
 * @return {JSX.Element} An anchor when href is present, else a span
 */
export const LinkOrText = ({ href, className, children }: LinkOrTextProps) => {
  if (!href) {
    return <span>{children}</span>;
  }

  return (
    <a
      href={href}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
};
